import { useEffect, useState, useRef } from 'react';

import MarketplaceProductCard from '@c/marketplace/MarketplaceProductCard';
import EmptyState from '@c/dashboard/EmptyState';
import { ShoppingBagIcon } from '@c/icons';
import { UIProductCardSkeleton } from 'blocks/product-card';
import Button from '@ui/Button';
import Paginator from '@ui/Paginator';
import { ProductDocument } from '@util/types/firestore/products';
import { Pagination } from '@util/types/paginator';
import { formatProductItems, trackEvent } from '@util/analytics';
import { useListId } from '@util/hooks/useListId';
import SearchEmptyState from './SearchEmptyState';

// Shared by the loaded grid and its skeleton so the two can't drift apart.
// Keep in sync with CategorySkeleton's grid.
const PRODUCT_GRID =
  'grid grid-cols-2 gap-[2rem] sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5';
const SKELETON_COUNT = 15;

export const PageMainContent = ({
  view,
  isFetching,
  products,
  pageInfo,
  content,
  filters,
  controls,
  hidePaginator,
  onPageChange,
  showMarketBadges = false,
  keyword = null,
  hasActiveFilters = false,
  showMasterLinks = false,
}: {
  view: 'grid' | 'list';
  isFetching: boolean;
  products: ProductDocument[];
  pageInfo: Pagination;
  content?: string;
  filters: React.ReactNode;
  controls: React.ReactNode;
  hidePaginator?: boolean;
  onPageChange: (page: number) => void;
  /** When true, show orange market badges instead of red sale badges (D&S page) */
  showMarketBadges?: boolean;
  /**
   * The active keyword search term, when this page is a keyword search
   * (e.g. /search). Category/brand/collection pages that don't search by
   * keyword pass nothing, and keep their existing zero-result fallback
   * below (GB #1401 is scoped to the keyword zero-result state only).
   */
  keyword?: string | null;
  /**
   * Whether the caller currently has any side filter (price/category/
   * condition/brand/deals/useCases/additional) applied. GB #1401 gate-review
   * fix (CRITICAL): without this, a keyword search with real inventory but
   * zero matches for the CURRENT filter/page combo rendered SearchEmptyState
   * and its "No listings for X right now" claim + a notify-me offer for gear
   * that IS in stock — and fired search_zero_results, corrupting the very
   * metric this feature is measured by. Defaults to false so every other
   * (non-search) caller of this component is unaffected.
   */
  hasActiveFilters?: boolean;
  /** When true, grid-view cards show a /m/ back-link if the listing has a matched GearMaster model (GB web#1005, category pages only — grid view only, list view unsupported) */
  showMasterLinks?: boolean;
}) => {
  const [isSticky, setIsSticky] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [barHeight, setBarHeight] = useState(0);
  const filterRef = useRef<HTMLDivElement | null>(null);
  const HEADER_HEIGHT = 280;

  // Measure the bar while it's still in flow, so a spacer can hold its place
  // once it goes fixed — otherwise the grid below jumps up by the bar's height.
  useEffect(() => {
    if (isSticky) return;
    const el = filterRef.current;
    if (!el) return;
    const measure = () => setBarHeight(el.offsetHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isSticky]);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsDesktop(window.innerWidth >= 750);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!isDesktop) {
        setIsSticky(false);
        return;
      }

      if (window.scrollY > HEADER_HEIGHT) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isDesktop]);

  // get item list id based on the current route
  const listId = useListId()  

  // Track view_item_list event when the page loads
  useEffect(() => {
    if (!products || products.length === 0) return;
    trackEvent("view_item_list", {
      item_list_name: listId,
      item_list_id: listId,
      items: formatProductItems(products, listId),
    });
  }, [products, listId, trackEvent])

  // Plain "No Results" fallback (GB #1401 N2 fix): previously only the grid
  // branch had this — list view rendered nothing at all for a keyword search
  // with real inventory that returns zero for the current filter/page combo.
  // Since /search's default view is `list` (the header search box always
  // navigates with ?view=list), that left the common filtered/paginated
  // zero-result case showing a totally blank page. Hoisted once here so both
  // the grid and list branches render the identical box instead of forking
  // the JSX.
  const plainNoResultsFallback = (
    <div className="order-1 flex h-full max-h-[calc(100vh_-_600px)] min-h-[30rem] w-screen grow flex-col items-center justify-center self-stretch sm:w-full">
      <div>
        <div className="mb-5">
          <EmptyState
            icon={<ShoppingBagIcon />}
            title={'No Results'}
            description={'Have one to sell?'}
          />
        </div>
        <Button
          text="Sell Yours"
          type="tertiary"
          href="/sell/create-listing"
        />
      </div>
    </div>
  );

  return (
    <div className="
      flex flex-col w-full
    ">
      {/* controls */}
      <div className="hidden sm:flex w-full gap-[0.8rem] lg:gap-[1rem]">
        {controls}
      </div>

      {/* top filters and results */}
      <div className="flex h-min w-full flex-col gap-[2rem] py-[0rem] sm:py-[2rem]">
        <div
          ref={filterRef}
          // Pin directly below the sticky header. --gf-header-h is published by
          // Navbar and tracks its scroll collapse + the xl-only CategoryNav, so
          // this stays flush instead of leaving a gap when the header changes.
          style={isSticky ? { top: 'var(--gf-header-h)' } : undefined}
          className={`
            w-full py-[0rem] lg:py-[1.4rem]
            ${isSticky ? "fixed left-0 bg-white shadow-md z-[40] pt-[3rem] pb-[1.5rem] lg:py-[1.4rem] animate-gf-slide-down" : ""}
          `}>
          {/* While fixed, the bar spans the viewport, so it needs the page's own
              container to line its chips up with the header and the grid below —
              mx-auto + max-w-default, matching Navbar/CategoryNav/page content. */}
          <div className={`
            gap-[1rem] lg:gap-0
            ${isSticky ? "mx-auto w-full max-w-default px-[2rem] md:px-[4rem] xl:px-default-padding-x" : ""}
          `}>
            {/* side filters */}
            <div className="w-full">{filters}</div>
          </div>
        </div>

        {/* Holds the bar's place in flow while it's fixed, so nothing shifts. */}
        {isSticky && barHeight > 0 && (
          <div aria-hidden="true" style={{ height: barHeight }} />
        )}

        {/* Zero-result keyword search (GB #1401, D1): one shared empty state
            for BOTH the grid and list views, hoisted above the view ternary
            so the two views don't each need their own copy of it. Gated on
            `keyword` — a category/brand/collection page with zero matches
            (no keyword) falls through to the view-specific handling below,
            which keeps its pre-existing behavior (grid: the old "No Results"
            card; list: the plain fallback below, added by the same fix that
            hoisted this block — see N2 below).

            Also gated on `!hasActiveFilters && pageInfo.totalResults === 0`
            (gate-review CRITICAL fix, corrected AGAIN post-Codex-review):
            zero products for the current filter/page combo is NOT the same
            thing as a genuine zero-result search. A search with real
            inventory, filtered down to zero (or paginated past the last
            page), falls through to the same view-specific fallback a
            filters-less zero-result page uses, rather than falsely claiming
            "no listings" and offering notify-me for gear that IS in stock.

            `totalResults === 0`, not a `page`-number heuristic: an earlier
            version of this gate used `pageInfo.page <= 1`, reasoning that
            products.service.ts's Algolia branch computes
            `page: args.page > res.nbPages ? res.nbPages : args.page`, and for
            a GENUINE zero-result search Algolia's res.nbPages is 0, so the
            computed page is 0, not 1 — `=== 1` never matched that real shape.
            But `<= 1` is ALSO wrong (Codex review, GB #1401): that same
            clamp fires whenever a request asks for an out-of-range page on a
            search that DOES have results. A search with real inventory but
            only one page of it (`nbPages === 1`), requested at `?page=999`,
            clamps right back down to `page: 1` — `pageInfo.page <= 1` is
            true, `products` is empty (nothing on that out-of-range page),
            and the old gate would have shown "No listings" for a model that
            IS in stock. `totalResults` is Algolia's authoritative "does this
            search have ANY results at all" signal — it's independent of
            which page was requested or how page numbers get clamped, so it
            is 0 for a genuine zero-result search and > 0 for the
            out-of-range-page-with-real-results case above, correctly
            distinguishing the two. */}
        {!isFetching &&
        products.length === 0 &&
        keyword &&
        !hasActiveFilters &&
        pageInfo.totalResults === 0 ? (
          <SearchEmptyState keyword={keyword} />
        ) : view === 'list' ? (
          <div className="flex h-full flex-col justify-center gap-[0.8rem] sm:gap-[1.2rem]">
            {isFetching ? (
                  <>
                    {Array.from({ length: 6 }).map((_, i) => (
                      <UIProductCardSkeleton key={i} variant="horizontal" />
                    ))}
                  </>
            ) : products.length ? (
              products.map((result) => (
                <MarketplaceProductCard
                  item={result}
                  key={result.id}
                  variant="list"
                  showMarketBadge={showMarketBadges}
                />
              ))
            ) : (
              plainNoResultsFallback
            )}
          </div>
        ) : (
          <div className="h-full w-full">
            {isFetching ? (
              // Same grid as the loaded state — otherwise the skeletons render as
              // plain blocks and stack in one column.
              <div className={PRODUCT_GRID}>
                {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                  <UIProductCardSkeleton key={i} />
                ))}
              </div>
            ) : products.length ? (
              <div className={PRODUCT_GRID}>
                {products.map((result, index) => (
                  <MarketplaceProductCard
                    item={result}
                    key={result.id}
                    priority={index < 2}
                    showMarketBadge={showMarketBadges}
                    showMasterLink={showMasterLinks}
                  />
                ))}
              </div>
            ) : (
              plainNoResultsFallback
            )}
          </div>
        )}

        {pageInfo.totalPages > 1 && !hidePaginator && (
          <Paginator
            currentPage={pageInfo?.page ?? 1}
            totalPages={pageInfo?.totalPages ?? 0}
            scroll={true}
            onPageChange={onPageChange}
          />
        )}
      </div>

      <div className="flex flex-col w-full max-w-default gap-[1.2rem]">
        {/* optional page content */}
        {content && (
          <div className="
            flex flex-col 
            justify-between gap-[1.6rem]
            overflow-x-hidden
            my-[4rem]
            p-[2rem] sm:p-[2.5rem]
            border-[1px] border-brand-light-gray rounded-[1rem]
          ">
            <span className="text-[16px] primary-links" dangerouslySetInnerHTML={{ __html: content }} />
          </div>
        )}
      </div>
    </div>
  );
};
